<template>
    <div class="qr-page" :class="{ 'qr-dark': isDark }" :style="{ paddingBottom: pagePadding }">
        <div class="qr-header">
            <!-- Тема спільна з сайтом (Vuex nightMode + localStorage 'nightmode'); без явного
                 вибору — як у системі телефона. Одна кнопка: місяць у світлій, сонце в темній -->
            <button type="button" class="qr-theme" :aria-label="isDark ? t('themeLight') : t('themeDark')" :title="isDark ? t('themeLight') : t('themeDark')" @click="changeMode">
                <inline-svg :src="isDark ? '/img/icons/sun.svg' : '/img/icons/moon.svg'" width="18" height="18"></inline-svg>
            </button>
            <!-- Мова: без збереженого вибору — за мовою телефона (все, крім uk/ru → EN) -->
            <button type="button" class="qr-lang" :aria-label="t('switchLang')" :title="t('switchLang')" @click="toggleLang">
                {{ lang === 'uk' ? 'EN' : 'UA' }}
            </button>
            <a class="qr-insta" href="https://www.instagram.com/black_pizza/" target="_blank" rel="noopener nofollow" aria-label="Instagram" title="Instagram">
                <inline-svg src="/img/icons/instagram.svg" width="18" height="18" fill="#fff"></inline-svg>
            </a>
            <a href="https://pizza.black/" class="qr-header-logo-link">
                <img src="@/assets/logo_site_white.webp" alt="Black Pizza" class="qr-header-logo">
            </a>
            <div class="qr-header-sub">{{ t('festMenu') }}</div>
        </div>

        <div v-if="loading" class="qr-loading">
            <img src="@/assets/loading.svg" :alt="t('loading')" style="width: 34px;">
        </div>

        <div v-else-if="ordered" class="qr-success">
            <div class="qr-success-check">✓</div>
            <div class="qr-success-title">{{ t('orderAccepted', { id: ordered.id }) }}</div>
            <div class="qr-success-comment">{{ ordered.comment }}</div>
            <div class="qr-success-hint">{{ t('successHint') }}</div>
            <div class="qr-success-actions">
                <div v-if="payError" class="qr-error qr-success-error">{{ payError }}</div>
                <button v-if="canPayOnline" class="qr-btn qr-btn-dark" :disabled="paying" @click="startPayment">
                    {{ paying ? t('goingToPay') : t('payOnlineShort') }}
                </button>
                <div v-if="canPayOnline" class="qr-pay-hint">{{ t('payHint') }}</div>
                <a v-if="ordered.checkUrl" class="qr-success-link" :href="ordered.checkUrl">{{ t('viewOrder') }}</a>
                <button class="qr-btn" :class="{ 'qr-btn-outline': canPayOnline }" @click="ordered = null">{{ t('newOrder') }}</button>
            </div>
        </div>

        <template v-else>
            <!-- QR веде на цю саму сторінку: гість показує екран сусідові за столом, і меню
                 відкривається без пересилання посилання. Адреса незмінна, тому SVG статичний -->
            <div class="qr-share">
                <div class="qr-share-heading">{{ t('qrShareHeading') }}</div>
                <div class="qr-share-url">pizza.black/qr</div>
                <img class="qr-share-code" src="/img/qr-menu.svg" :alt="t('qrShareAlt')">
            </div>

            <div class="qr-products">
                <div v-for="group in groups" :key="group.key" class="qr-section">
                    <div class="qr-section-title">{{ group.title }}</div>
                    <div v-for="product in group.items" :key="product.id" class="qr-card" :data-id="product.id">
                        <img class="qr-card-image" :src="product.thumbnail" :alt="productTitle(product)" loading="lazy">
                        <div class="qr-card-body">
                            <div class="qr-card-title">{{ productTitle(product) }}</div>
                            <div class="qr-card-description">{{ descriptionLine(product) }}</div>
                            <div class="qr-card-bottom">
                                <div class="qr-card-price">{{ money(product.price) }}</div>
                                <button v-if="!cart[product.id]" type="button" class="qr-add-button" :aria-label="t('add') + ' ' + productTitle(product)" :title="t('add') + ' ' + productTitle(product)" @click="add(product.id)">
                                    <inline-svg src="/img/icons/shopping-cart.svg" width="22" height="22" fill="#fff" class="qr-add-icon"></inline-svg>
                                </button>
                                <div v-else class="qr-stepper qr-pop">
                                    <div class="qr-stepper-btn" @click="remove(product.id)">−</div>
                                    <div class="qr-stepper-count">{{ cart[product.id] }}</div>
                                    <div class="qr-stepper-btn" @click="add(product.id)">+</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="!products.length" class="qr-empty">{{ t('menuUnavailable') }}</div>
            </div>

            <!-- Панель виїжджає знизу; add() підкручує сторінку, щоб доданий товар не сховався під нею -->
            <transition name="qr-slide">
                <div v-if="totalCount" ref="checkout" class="qr-checkout">
                    <div class="qr-checkout-head">
                        <div class="qr-checkout-title">
                            {{ t('cart') }}
                            <span class="qr-checkout-count">{{ totalCount }} {{ countLabel }}</span>
                        </div>
                        <button type="button" class="qr-clear" @click="clear">
                            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 10v6M14 10v6"/>
                            </svg>
                            {{ t('clear') }}
                        </button>
                    </div>
                    <ul class="qr-cart-list">
                        <li v-for="line in cartLines" :key="line.id" class="qr-cart-line">
                            <span class="qr-cart-line-title">{{ line.title }} <b>×{{ line.quanity }}</b></span>
                            <span class="qr-cart-line-sum">{{ money(line.sum) }}</span>
                        </li>
                    </ul>
                    <div class="qr-checkout-total">
                        <span>{{ t('total') }}</span>
                        <b>{{ money(totalPrice) }}</b>
                    </div>
                    <input
                        v-model="comment"
                        class="qr-comment"
                        :class="{'qr-comment-required': commentMissing}"
                        type="text"
                        maxlength="200"
                        autocomplete="name"
                        :placeholder="t('namePlaceholder')">
                    <div v-if="error" class="qr-error">{{ error }}</div>
                    <template v-if="onlinePay">
                        <button class="qr-btn qr-btn-dark" :disabled="!!submitting" @click="submit(true)">
                            {{ submitting === 'online' ? t('sending') : t('payOnline', { sum: money(totalPrice) }) }}
                        </button>
                        <div class="qr-pay-hint">{{ t('payHint') }}</div>
                        <button class="qr-btn qr-btn-outline" :disabled="!!submitting" @click="submit(false)">
                            {{ submitting === 'cash' ? t('sending') : t('orderCash') }}
                        </button>
                    </template>
                    <button v-else class="qr-btn" :disabled="!!submitting" @click="submit(false)">
                        {{ submitting ? t('sending') : t('orderFor', { sum: money(totalPrice) }) }}
                    </button>
                </div>
            </transition>
        </template>
    </div>
</template>

<script>
import axios from 'axios'
import Vue from 'vue'
import InlineSvg from 'vue-inline-svg'

// Фест працює лише в Луцьку; замовлення падають в окрему локацію Fest,
// на яку дивиться кухонна каса. id зафіксований міграцією бекенда.
const FEST_LOCATION_ID = 4
const STORAGE_KEY = 'qrCart'
// Тема — спільна з сайтом: Vuex nightMode + localStorage 'nightmode' (див. changeMode у LocationCityBase.vue)
const NIGHT_MODE_KEY = 'nightmode'
const NIGHT_MODE_BG = '#1c1a1a'
const LANG_KEY = 'qrLang'

// Тексти інтерфейсу. Назви й склад позицій приходять з БД українською — для EN є
// словники нижче (фест-меню невелике й керується власником).
const MESSAGES = {
    uk: {
        festMenu: 'Фест-меню',
        loading: 'Завантаження',
        pizzas: 'Піци',
        drinks: 'Напої',
        add: 'Додати',
        cart: 'Корзина',
        clear: 'Очистити',
        total: 'Разом',
        namePlaceholder: 'Ваше імʼя, щоб покликати Вас :)',
        payOnline: 'Оплатити онлайн {sum}',
        payOnlineShort: 'Оплатити онлайн',
        payHint: 'Apple Pay · Google Pay · картка',
        orderCash: 'Замовити, оплата на касі',
        orderFor: 'Замовити на {sum}',
        sending: 'Надсилаємо…',
        goingToPay: 'Переходимо до оплати…',
        menuUnavailable: 'Меню тимчасово недоступне',
        orderAccepted: 'Замовлення №{id} прийнято!',
        successHint: 'Покажіть цей екран або назвіть імʼя на видачі',
        viewOrder: 'Побачити замовлення',
        newOrder: 'Нове замовлення',
        needName: 'Впишіть ваше імʼя — без цього не покличемо вас на видачі',
        sendFail: 'Не вдалося надіслати замовлення. Спробуйте ще раз або підійдіть до каси.',
        payUnavailable: 'Онлайн-оплата зараз недоступна — оплатіть на касі',
        pay409: 'Онлайн-оплата для цього замовлення недоступна — оплатіть на касі',
        pay429: 'Забагато спроб оплати. Спробуйте трохи пізніше або оплатіть на касі',
        pay503: 'Оплата тимчасово недоступна — оплатіть на касі',
        payFail: 'Не вдалося почати оплату. Спробуйте ще раз або оплатіть на касі',
        themeLight: 'Світла тема',
        themeDark: 'Темна тема',
        switchLang: 'Switch to English',
        qrShareHeading: 'Меню',
        qrShareAlt: 'QR-код на це меню',
        currency: 'грн',
        ml: 'мл',
    },
    en: {
        festMenu: 'Fest menu',
        loading: 'Loading',
        pizzas: 'Pizzas',
        drinks: 'Drinks',
        add: 'Add',
        cart: 'Cart',
        clear: 'Clear',
        total: 'Total',
        namePlaceholder: 'Your name, so we can call you :)',
        payOnline: 'Pay online {sum}',
        payOnlineShort: 'Pay online',
        payHint: 'Apple Pay · Google Pay · card',
        orderCash: 'Order, pay at the counter',
        orderFor: 'Order for {sum}',
        sending: 'Sending…',
        goingToPay: 'Opening payment…',
        menuUnavailable: 'The menu is temporarily unavailable',
        orderAccepted: 'Order #{id} accepted!',
        successHint: 'Show this screen or say your name at the pickup counter',
        viewOrder: 'View order',
        newOrder: 'New order',
        needName: 'Please enter your name — we need it to call you',
        sendFail: 'Could not send the order. Try again or come to the counter.',
        payUnavailable: 'Online payment is unavailable right now — please pay at the counter',
        pay409: 'Online payment is unavailable for this order — please pay at the counter',
        pay429: 'Too many payment attempts. Try again in a minute or pay at the counter',
        pay503: 'Payment is temporarily unavailable — please pay at the counter',
        payFail: 'Could not start the payment. Try again or pay at the counter',
        themeLight: 'Light theme',
        themeDark: 'Dark theme',
        switchLang: 'Перейти на українську',
        qrShareHeading: 'Menu',
        qrShareAlt: 'QR code for this menu',
        currency: 'UAH',
        ml: 'ml',
    },
}

const PRODUCT_EN = {
    'Войт': 'Voit',
    'Хантер': 'Hunter',
    "П'ять сирів": 'Five Cheeses',
    'Пʼять сирів': 'Five Cheeses',
    'Формаджі': 'Formaggi',
    'Капрічоза': 'Capricciosa',
    'Гаваї': 'Hawaii',
    'Альпійська': 'Alpine',
    'Каріна': 'Karina',
    'Качаторе': 'Cacciatore',
    'Маргарита': 'Margherita',
    'Везувіо': 'Vesuvio',
    'Фунгі': 'Funghi',
    'Сік Sandora': 'Sandora juice',
}

// Інгредієнти у складі перелічені через кому; невідомі слова лишаються як є
const INGREDIENT_EN = {
    'вершки': 'cream',
    'сир': 'cheese',
    'салямі': 'salami',
    'шинка': 'ham',
    'курка': 'chicken',
    'кукурудза': 'corn',
    'помідори чері': 'cherry tomatoes',
    'помідори': 'tomatoes',
    'томати': 'tomatoes',
    'орегано': 'oregano',
    'томатний соус': 'tomato sauce',
    'мисливські ковбаски': 'hunter sausages',
    'бекон': 'bacon',
    'цибуля': 'onion',
    'моцарела': 'mozzarella',
    'дорблю': 'dorblu blue cheese',
    'пармезан': 'parmesan',
    'чеддер': 'cheddar',
    'чедер': 'cheddar', // в БД трапляються обидва написання
    'фета': 'feta',
    'печериці': 'mushrooms',
    'гриби': 'mushrooms',
    'ананаси': 'pineapple',
    'ананас': 'pineapple',
    'оливки': 'olives',
    'маслини': 'black olives',
    'перець': 'pepper',
    'болгарський перець': 'bell pepper',
    'халапеньо': 'jalapeño',
    'часник': 'garlic',
    'часниковий соус': 'garlic sauce',
    'вершковий соус': 'cream sauce',
    'сирний соус': 'cheese sauce',
    'соус': 'sauce',
    'базилік': 'basil',
    'руккола': 'arugula',
    'яйце': 'egg',
    'огірки мариновані': 'pickles',
    'пепероні': 'pepperoni',
    'ковбаса': 'sausage',
    'телятина': 'veal',
    'свинина': 'pork',
    'індичка': 'turkey',
    'тунець': 'tuna',
    'креветки': 'shrimps',
    'лосось': 'salmon',
    'гірчиця': 'mustard',
    'майонез': 'mayonnaise',
    'кетчуп': 'ketchup',
    'зелень': 'herbs',
    'кунжут': 'sesame',
    'сирні бортики': 'cheese crust',
}

export default {
    name: 'qr-menu',

    components: {
        InlineSvg,
    },

    data() {
        return {
            products: [],
            cart: {},
            comment: '',
            loading: true,
            // '' | 'online' | 'cash' — яку кнопку натиснули, поки летить запит
            submitting: '',
            ordered: null,
            // Кнопки онлайн-оплати вмикає сервер (/api/qr-menu → online_pay), коли всі позиції
            // заведені в каталозі Checkbox; інакше фіскальний чек після оплати був би без позицій
            onlinePay: false,
            paying: false,
            payError: '',
            error: '',
            commentMissing: false,
            // Висота фіксованої панелі корзини — на стільки відступаємо знизу,
            // щоб вона не налазила на останню позицію меню
            checkoutHeight: 0,
            // Тема системи телефона (prefers-color-scheme) — діє, поки користувач не обрав сам
            systemDark: false,
            lang: 'uk',
        }
    },

    computed: {
        totalCount() {
            return Object.values(this.cart).reduce((sum, quanity) => sum + quanity, 0)
        },

        totalPrice() {
            return Object.entries(this.cart).reduce((sum, [id, quanity]) => {
                const product = this.products.find(p => p.id == id)
                return product ? sum + product.price * quanity : sum
            }, 0)
        },

        countLabel() {
            if (this.lang === 'en') {
                return this.totalCount === 1 ? 'item' : 'items'
            }
            const n10 = this.totalCount % 10
            const n100 = this.totalCount % 100
            if (n10 == 1 && n100 != 11) return 'позиція'
            if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return 'позиції'
            return 'позицій'
        },

        pagePadding() {
            return (this.checkoutHeight ? this.checkoutHeight + 16 : 24) + 'px'
        },

        isDark() {
            const chosen = this.$store.state.nightMode
            if (chosen === null || chosen === undefined) {
                return this.systemDark
            }
            return !!chosen
        },

        cartLines() {
            return this.products
                .filter(p => this.cart[p.id])
                .map(p => ({
                    id: p.id,
                    title: this.productTitle(p),
                    quanity: this.cart[p.id],
                    sum: p.price * this.cart[p.id],
                }))
        },

        canPayOnline() {
            return this.onlinePay && !!(this.ordered && this.ordered.checkSig)
        },

        // Напої відрізняємо за об'ємом — так само, як при відправці замовлення
        groups() {
            const drinks = this.products.filter(p => p.volume > 0)
            const pizzas = this.products.filter(p => !(p.volume > 0))

            return [
                { key: 'pizzas', title: this.t('pizzas'), items: pizzas },
                { key: 'drinks', title: this.t('drinks'), items: drinks },
            ].filter(group => group.items.length)
        },
    },

    watch: {
        cart: { handler() { this.persist() }, deep: true },
        isDark: {
            // Фон за межами .qr-page (гумове прокручування на iOS) — у колір теми
            handler(dark) {
                document.documentElement.style.backgroundColor = dark ? NIGHT_MODE_BG : ''
                document.body.style.backgroundColor = dark ? NIGHT_MODE_BG : ''
            },
            immediate: true,
        },
        comment() {
            this.persist()
            if (this.comment.trim()) {
                this.commentMissing = false
            }
        },
    },

    mounted() {
        this.restore()
        this.restoreLang()
        this.watchSystemTheme()
        window.addEventListener('resize', this.measureCheckout)

        axios.get(`${Vue.prototype.SERVER_URL}/api/qr-menu`).then(response => {
            this.products = response.data.products
            this.onlinePay = !!response.data.online_pay

            // Викидаємо з кошика позиції, яких у меню вже нема
            Object.keys(this.cart).forEach(id => {
                if (!this.products.find(p => p.id == id)) {
                    this.$delete(this.cart, id)
                }
            })

            this.loading = false
        }).catch(() => {
            this.products = []
            this.loading = false
        })
    },

    // Панель з'являється/зникає і змінює висоту (помилка, кількість рядків) —
    // переміряємо після кожного перерендеру; однакове значення не запускає новий цикл
    updated() {
        this.measureCheckout()
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.measureCheckout)
        if (this.darkQuery) {
            if (this.darkQuery.removeEventListener) {
                this.darkQuery.removeEventListener('change', this.onSystemTheme)
            } else if (this.darkQuery.removeListener) {
                this.darkQuery.removeListener(this.onSystemTheme)
            }
        }
        document.documentElement.style.backgroundColor = ''
    },

    methods: {
        measureCheckout() {
            const panel = this.$refs.checkout
            this.checkoutHeight = panel ? panel.offsetHeight : 0
        },

        t(key, params) {
            const dict = MESSAGES[this.lang] || MESSAGES.uk
            let text = dict[key] !== undefined ? dict[key] : (MESSAGES.uk[key] || key)
            Object.keys(params || {}).forEach(name => {
                text = text.replace(`{${name}}`, params[name])
            })
            return text
        },

        money(sum) {
            return `${sum} ${this.t('currency')}`
        },

        productTitle(product) {
            const title = (product.title || '').trim()
            if (this.lang === 'en') {
                return PRODUCT_EN[title] || title
            }
            return title
        },

        // Склад «вершки, сир x2, …» → пословний переклад інгредієнтів для EN
        translateDescription(description) {
            return description.split(',').map(part => {
                const match = part.trim().match(/^(.*?)(\s*[x×]\s*\d+)?$/i)
                const base = (match ? match[1] : part).trim()
                const suffix = match && match[2] ? match[2] : ''
                const translated = INGREDIENT_EN[base.toLowerCase()]
                return (translated || base) + suffix
            }).join(', ')
        },

        // Рядок під назвою: для піци — склад, для напою — об'єм у мілілітрах
        // («750 мл»), а опис із БД, якщо він є, — після нього
        descriptionLine(product) {
            const ml = Number(product.volume)
            let description = (product.description || '').trim()

            if (this.lang === 'en' && description) {
                description = this.translateDescription(description)
            }

            if (!(ml > 0)) {
                return description
            }

            const volume = `${ml} ${this.t('ml')}`
            return description ? `${volume} · ${description}` : volume
        },

        restoreLang() {
            let saved = null
            try {
                saved = localStorage.getItem(LANG_KEY)
            } catch (e) { /* без сховища — за мовою телефона */ }

            if (saved === 'uk' || saved === 'en') {
                this.lang = saved
                return
            }

            const phone = String(navigator.language || navigator.userLanguage || '').toLowerCase()
            this.lang = (phone.startsWith('uk') || phone.startsWith('ru') || !phone) ? 'uk' : 'en'
        },

        toggleLang() {
            this.lang = this.lang === 'uk' ? 'en' : 'uk'
            try {
                localStorage.setItem(LANG_KEY, this.lang)
            } catch (e) { /* приватний режим — мова живе до перезавантаження */ }
        },

        clear() {
            this.cart = {}
            this.error = ''
            this.commentMissing = false
        },

        watchSystemTheme() {
            if (!window.matchMedia) {
                return
            }
            this.darkQuery = window.matchMedia('(prefers-color-scheme: dark)')
            this.systemDark = this.darkQuery.matches
            this.onSystemTheme = event => { this.systemDark = event.matches }
            if (this.darkQuery.addEventListener) {
                this.darkQuery.addEventListener('change', this.onSystemTheme)
            } else if (this.darkQuery.addListener) {
                this.darkQuery.addListener(this.onSystemTheme)
            }
        },

        // Як на сайті: 0/1 у localStorage і в store (явний вибір перекриває системну тему),
        // фон body ставить watcher isDark
        changeMode() {
            const newMode = this.isDark ? 0 : 1
            try {
                localStorage.setItem(NIGHT_MODE_KEY, newMode)
            } catch (e) { /* приватний режим — тема живе до перезавантаження */ }
            this.$store.commit('setMode', newMode)
        },

        add(id) {
            this.$set(this.cart, id, (this.cart[id] || 0) + 1)
            this.$nextTick(() => this.revealCard(id))
        },

        // Панель корзини фіксована знизу: якщо доданий товар опинився під нею —
        // плавно підкручуємо сторінку, щоб гість бачив, що саме додав
        revealCard(id) {
            this.measureCheckout()
            // Ще один тік: після заміру відступ сторінки знизу вже застосовано,
            // інакше в кінці списку прокручувати просто нікуди
            this.$nextTick(() => {
                const card = this.$el.querySelector(`.qr-card[data-id="${id}"]`)
                if (!card || !this.checkoutHeight) {
                    return
                }
                const panelTop = window.innerHeight - this.checkoutHeight
                const rect = card.getBoundingClientRect()
                if (rect.bottom > panelTop - 8) {
                    window.scrollBy({ top: rect.bottom - panelTop + 16, behavior: 'smooth' })
                }
            })
        },

        remove(id) {
            const next = (this.cart[id] || 0) - 1
            if (next > 0) {
                this.$set(this.cart, id, next)
            } else {
                this.$delete(this.cart, id)
            }
        },

        persist() {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify({ cart: this.cart, comment: this.comment }))
            } catch (e) { /* приватний режим — живемо без збереження */ }
        },

        restore() {
            try {
                const saved = JSON.parse(localStorage.getItem(STORAGE_KEY))
                if (saved && saved.cart) {
                    this.cart = saved.cart
                    this.comment = saved.comment || ''
                }
            } catch (e) { /* зіпсоване сховище — просто порожній кошик */ }
        },

        submit(payOnline) {
            if (!this.comment.trim()) {
                this.commentMissing = true
                this.error = this.t('needName')
                return
            }

            this.error = ''
            this.submitting = payOnline ? 'online' : 'cash'

            const orderedProducts = Object.entries(this.cart).map(([id, quanity]) => {
                const product = this.products.find(p => p.id == id)
                return {
                    id: product.id,
                    quanity: quanity,
                    // «30» (не «30 см»): бек рахує суму позицій з нестандартним
                    // розміром із ціни самої позиції — для фест-цін це саме те
                    size: product.volume > 0 ? `${product.volume} ml` : '30',
                    price: product.price,
                    priceWithoutAdditionals: product.price,
                    additionaIngredients: [],
                }
            })

            const body = {
                order: {
                    type: 'take-away',
                    name: 'QR',
                    phone: '0000000000',
                    payment_type: 'Готівка',
                    comment: this.comment.trim(),
                    call_back: false,
                    discount: 0,
                    bonuses_used: 0,
                    city: null,
                    street: '',
                    house: '',
                    apartment: '',
                    location_id: FEST_LOCATION_ID,
                    delivery_at: null,
                },
                products: orderedProducts,
            }

            axios.post(`${Vue.prototype.SERVER_URL}/api/1/order`, body).then(response => {
                const data = response.data || {}
                const id = Number(data.id)

                // Підписане посилання на /check бек віддає лише фест-замовленням; беремо його
                // тільки у перевіреному вигляді (16 hex і наш домен) і тримаємо лише в пам'яті —
                // persist() зберігає тільки корзину й коментар
                const checkSig = (typeof data.check_sig === 'string' && /^[0-9a-f]{16}$/.test(data.check_sig))
                    ? data.check_sig
                    : ''
                const checkUrl = (typeof data.check_url === 'string' && data.check_url.startsWith('https://pizza.black/check/'))
                    ? data.check_url
                    : ''

                this.ordered = {
                    id: id,
                    comment: this.comment.trim(),
                    checkSig: Number.isInteger(id) && id > 0 ? checkSig : '',
                    checkUrl: checkUrl,
                }
                this.cart = {}
                this.comment = ''
                this.submitting = ''
                this.payError = ''
                window.scrollTo(0, 0)

                if (payOnline) {
                    if (this.canPayOnline) {
                        this.startPayment()
                    } else {
                        this.payError = this.t('payUnavailable')
                    }
                }
            }).catch(e => {
                this.error = (e.response && e.response.data && e.response.data.status)
                    ? e.response.data.status
                    : this.t('sendFail')
                this.submitting = ''
            })
        },

        // Оплата — тим самим шляхом, що й зі сторінки чека /check: POST /pay готує дані
        // LiqPay у замовленні, далі віджет-сторінка сервера (Apple Pay / Google Pay / картка
        // малює LiqPay), після оплати LiqPay повертає на /check/{id}/{sig}?paid=1.
        startPayment() {
            const id = this.ordered ? this.ordered.id : null
            const sig = this.ordered ? this.ordered.checkSig : ''

            if (!(Number.isInteger(id) && id > 0) || !/^[0-9a-f]{16}$/.test(sig)) {
                this.payError = this.t('payUnavailable')
                return
            }

            this.paying = true
            this.payError = ''

            axios.post(`${Vue.prototype.SERVER_URL}/api/check/${id}/${sig}/pay`, {}).then(() => {
                window.location.assign(`${Vue.prototype.SERVER_URL}/payment/liqpay/${encodeURIComponent(String(id))}`)
            }).catch(e => {
                this.paying = false
                const status = e.response && e.response.status

                if (status === 409) {
                    this.payError = this.t('pay409')
                } else if (status === 429) {
                    this.payError = this.t('pay429')
                } else if (status === 503) {
                    this.payError = this.t('pay503')
                } else {
                    this.payError = this.t('payFail')
                }
            })
        },
    },
}
</script>

<style scoped>
.qr-page {
    /* світла тема */
    --bg: #fff;
    --text: #111;
    --muted: #888;
    --line: rgba(0, 0, 0, 0.08);
    --image-bg: #f4f4f4;
    --accent: #AB2321;
    --accent-ink: #AB2321;
    --accent-soft: rgba(171, 35, 33, 0.35);
    --accent-tint: rgba(171, 35, 33, 0.08);
    --panel: rgba(255, 255, 255, 0.82);
    --panel-solid: #fff;
    --panel-line: rgba(0, 0, 0, 0.1);
    --panel-shadow: rgba(0, 0, 0, 0.12);
    --field-bg: #fff;
    --field-border: #ccc;
    --field-border-focus: #888;
    --error: #AB2321;
    --header-bg: #000;
    --control-bg: #D8D8D8;
    --control-border: #D8D8D8;
    --control-text: #111;

    max-width: 560px;
    min-height: 100vh;
    margin: 0 auto;
    padding-bottom: 24px; /* реальний відступ під панель корзини задається inline */
    text-align: left;
    color: var(--text);
    background: var(--bg);
}

.qr-page.qr-dark {
    /* кольори нічного режиму сайту (App.vue .dark): фон #1c1a1a, лінії #ffffff14, акцент #fb3431 */
    --bg: #1c1a1a;
    --text: #fff;
    --muted: #9a9a9a;
    --line: rgba(255, 255, 255, 0.08);
    --image-bg: #262323;
    --accent-ink: #fb3431;
    --accent-soft: rgba(251, 52, 49, 0.45);
    --accent-tint: rgba(251, 52, 49, 0.12);
    --panel: rgba(28, 26, 26, 0.84);
    --panel-solid: #1c1a1a;
    --panel-line: rgba(255, 255, 255, 0.08);
    --panel-shadow: rgba(0, 0, 0, 0.6);
    --field-bg: #262323;
    --field-border: rgba(255, 255, 255, 0.14);
    --field-border-focus: rgba(255, 255, 255, 0.4);
    --error: #fb3431;
    --header-bg: #1c1a1a;
    --control-bg: #000;
    --control-border: rgba(255, 255, 255, 0.18);
    --control-text: #fff;
}

.qr-header {
    position: relative;
    padding: 18px 12px 14px;
    border-bottom: 1px solid var(--line);
    background: var(--header-bg);
    color: #fff;
    text-align: center;
}

.qr-header-logo {
    display: block;
    width: 190px;
    max-width: calc(100vw - 200px); /* між кнопками в кутах (по ~90 px з боків) */
    margin: 0 auto 6px;
}

.qr-header-logo-link {
    display: block;
    line-height: 0;
}

.qr-header-sub {
    margin-top: 2px;
    font-size: 0.9rem;
    opacity: 0.85;
}

/* Кнопки в кутах шапки: прозорі кружки з білою іконкою — не сперечаються з логотипом */
.qr-theme,
.qr-lang,
.qr-insta {
    position: absolute;
    top: 14px;

    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    width: 36px;
    height: 36px;
    padding: 0;
    border: 1px solid rgba(255, 255, 255, 0.22);
    border-radius: 50%;

    color: #fff;
    line-height: 0;
    text-decoration: none;

    background: rgba(255, 255, 255, 0.08);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}

.qr-theme {
    left: 12px;
}

.qr-lang {
    left: 54px;
    font-family: inherit;
    font-size: 0.78rem;
    font-weight: 800;
    letter-spacing: 0.5px;
    line-height: 1;
}

.qr-insta {
    right: 12px;
}

/* Іконки сайту мають fill="white" у самому SVG — перефарбовуємо під колір кнопки */
.qr-theme >>> svg path {
    fill: currentColor;
}

.qr-loading {
    padding: 60px 0;
    text-align: center;
}

/* Блок з QR під шапкою: заголовок, адреса, і код на всю ширину сторінки.
   Код завжди чорний на білому — так він сканується і в темній темі, тому
   підкладка біла незалежно від --bg. Своя тиха зона вже є в самому SVG */
.qr-share {
    margin: 14px 12px 0;
    text-align: center;
}

.qr-share-heading {
    font-size: 1.3rem;
    font-weight: 900;
    letter-spacing: 0.3px;
}

.qr-share-url {
    margin-top: 2px;
    color: var(--muted);
    font-size: 0.9rem;
}

.qr-share-code {
    display: block;
    width: 100%;
    max-width: 168px; /* більше не треба: 29 модулів по ~5.8 px сканується з відстані руки */
    height: auto;
    margin: 8px auto 0;
    border-radius: 10px;
    background: #fff;
}

.qr-products {
    padding: 4px 12px 10px;
}

.qr-section + .qr-section {
    margin-top: 8px;
}

.qr-section-title {
    padding: 14px 0 2px;
    font-size: 1.3rem;
    font-weight: 900;
    letter-spacing: 0.3px;
}

.qr-card {
    display: flex;
    gap: 12px;

    padding: 12px 0;
    border-bottom: 1px solid var(--line);
}

.qr-card:last-child {
    border-bottom: none;
}

.qr-card-image {
    width: 92px;
    height: 92px;
    flex-shrink: 0;

    box-sizing: border-box;
    padding: 7px; /* картинки мають різні поля навколо піци — відступ вирівнює їх візуально */
    border-radius: 10px;
    object-fit: cover;
    background: var(--image-bg);
    transition: transform 0.35s ease, box-shadow 0.35s ease;
}

/* Наведення є лише там, де є курсор; на телефоні — те саме при дотику до картки */
@media (hover: hover) {
    .qr-card:hover .qr-card-image {
        transform: scale(1.12) rotate(-3deg);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18);
    }
}

.qr-card:active .qr-card-image {
    transform: scale(1.08);
}

.qr-card-body {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-width: 0;
}

.qr-card-title {
    font-size: 1.05rem;
    font-weight: 700;
}

.qr-card-description {
    margin-top: 2px;

    color: var(--muted);
    font-size: 0.8rem;
    line-height: 1.3;
}

.qr-card-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;

    margin-top: auto;
    padding-top: 6px;
}

.qr-card-price {
    font-size: 1.05rem;
    font-weight: 700;
}

/* Кнопка «в корзину»: іконка кошика сайту, легкий підйом і підстрибування при наведенні,
   натискання — стискається; на тачі лишається лише натискання */
.qr-add-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;

    width: 46px;
    height: 36px;
    padding: 0;
    border: none;
    border-radius: 8px;

    color: #fff;

    background: var(--accent);
    cursor: pointer;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    transition: transform 0.15s ease, box-shadow 0.2s ease;
}

.qr-add-icon {
    display: block;
}

@media (hover: hover) {
    .qr-add-button:hover {
        transform: translateY(-1px) scale(1.06);
        box-shadow: 0 6px 14px rgba(171, 35, 33, 0.35);
    }

    .qr-add-button:hover .qr-add-icon {
        animation: qr-cart-bounce 0.5s ease;
    }
}

.qr-add-button:active {
    transform: scale(0.9);
}

@keyframes qr-cart-bounce {
    0%   { transform: translateY(0); }
    30%  { transform: translateY(-3px) rotate(-6deg); }
    60%  { transform: translateY(1px) rotate(4deg); }
    100% { transform: translateY(0); }
}

/* Степпер «−  1  +» з'являється замість кнопки з легким «pop» */
.qr-pop {
    animation: qr-pop 0.25s cubic-bezier(0.2, 0.8, 0.2, 1.2);
}

@keyframes qr-pop {
    from { transform: scale(0.8); opacity: 0.4; }
    to   { transform: scale(1); opacity: 1; }
}

.qr-stepper {
    display: flex;
    align-items: center;
    gap: 4px;
}

.qr-stepper-btn {
    width: 36px;
    height: 36px;

    display: flex;
    align-items: center;
    justify-content: center;

    border: 1px solid var(--accent-ink);
    border-radius: 8px;

    color: var(--accent-ink);
    font-size: 1.3rem;
    font-weight: 700;
    line-height: 1;

    cursor: pointer;
    user-select: none;
}

.qr-stepper-count {
    min-width: 30px;
    text-align: center;
    font-size: 1.05rem;
    font-weight: 700;
}

.qr-empty {
    padding: 60px 0;
    color: var(--muted);
    font-weight: 700;
    text-align: center;
}

.qr-checkout {
    position: fixed;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;

    width: 100%;
    max-width: 560px;

    padding: 12px 12px calc(14px + env(safe-area-inset-bottom));
    border-top: 1px solid var(--panel-line);
    border-radius: 14px 14px 0 0;

    /* «Матове скло»: меню трохи просвічує з-під панелі */
    color: var(--text);
    background: var(--panel);
    -webkit-backdrop-filter: blur(16px) saturate(1.4);
    backdrop-filter: blur(16px) saturate(1.4);
    box-shadow: 0 -6px 24px var(--panel-shadow);
}

@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
    .qr-checkout {
        background: var(--panel-solid);
    }
}

/* Панель виїжджає знизу і так само ховається */
.qr-slide-enter-active,
.qr-slide-leave-active {
    transition: transform 0.3s ease, opacity 0.3s ease;
}

.qr-slide-enter,
.qr-slide-leave-to {
    transform: translate(-50%, 110%);
    opacity: 0;
}

.qr-checkout-head {
    display: flex;
    justify-content: space-between;
    align-items: center;

    margin-bottom: 4px;
}

.qr-checkout-title {
    font-size: 1.05rem;
    font-weight: 800;
}

.qr-checkout-count {
    margin-left: 4px;
    color: var(--muted);
    font-size: 0.85rem;
    font-weight: 600;
}

.qr-clear {
    display: inline-flex;
    align-items: center;
    gap: 6px;

    padding: 7px 12px;
    border: 1px solid var(--accent-soft);
    border-radius: 8px;

    color: var(--accent-ink);
    font-family: inherit;
    font-size: 0.85rem;
    font-weight: 700;
    line-height: 1;

    background: var(--accent-tint);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}

.qr-clear:active {
    transform: scale(0.96);
}

/* Що саме в корзині — коротким списком; довгий список прокручується всередині панелі */
.qr-cart-list {
    max-height: 26vh;
    margin: 0;
    padding: 0;
    overflow-y: auto;
    list-style: none;
}

.qr-cart-line {
    display: flex;
    justify-content: space-between;
    gap: 10px;

    padding: 7px 0;
    border-bottom: 1px dashed var(--line);
    font-size: 0.95rem;
}

.qr-cart-line-title b {
    margin-left: 2px;
    color: var(--muted);
    font-weight: 700;
}

.qr-cart-line-sum {
    white-space: nowrap;
    font-weight: 600;
}

.qr-checkout-total {
    display: flex;
    justify-content: space-between;
    align-items: center;

    margin-bottom: 10px;
    padding-top: 8px;
    font-size: 1.05rem;
}

.qr-comment {
    box-sizing: border-box;
    width: 100%;
    height: 44px;

    padding: 9px 12px;
    border: 1px solid var(--field-border);
    border-radius: 8px;

    color: var(--text);
    font-family: inherit;
    font-size: 1rem;
    background: var(--field-bg);
    resize: none;
}

.qr-comment::placeholder {
    color: var(--muted);
}

.qr-comment:focus {
    outline: none;
    border-color: var(--field-border-focus);
}

.qr-comment-required {
    border-color: var(--accent-ink);
    box-shadow: 0 0 0 0.2rem var(--accent-tint);
}

.qr-error {
    margin-top: 6px;
    color: var(--error);
    font-size: 0.85rem;
    font-weight: 600;
}

.qr-btn {
    width: 100%;

    margin-top: 8px;
    padding: 13px;
    border: none;
    border-radius: 8px;

    color: #fff;
    font-size: 1.1rem;
    font-weight: 700;

    background: var(--accent);
    cursor: pointer;
}

.qr-btn:disabled {
    opacity: 0.6;
}

.qr-btn-outline {
    border: 1px solid var(--accent-ink);
    color: var(--accent-ink);
    background: transparent;
}

/* Чорна кнопка онлайн-оплати; у темній темі — з тонкою рамкою, щоб не зливалася з фоном */
.qr-btn-dark {
    border: 1px solid var(--control-border);
    color: #fff;
    background: #000;
}

.qr-pay-hint {
    margin-top: 6px;
    color: var(--muted);
    font-size: 0.78rem;
    text-align: center;
}

.qr-success {
    padding: 70px 24px 40px;
    text-align: center;
}

.qr-success-check {
    width: 74px;
    height: 74px;

    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 18px;

    border-radius: 50%;

    color: #fff;
    font-size: 2.4rem;

    background: #2e9e44;
}

.qr-success-title {
    font-size: 1.4rem;
    font-weight: 900;
}

.qr-success-comment {
    margin-top: 10px;
    font-size: 1.15rem;
    font-weight: 700;
}

.qr-success-hint {
    margin-top: 8px;
    color: var(--muted);
    font-size: 0.9rem;
}

.qr-success-actions {
    max-width: 300px;
    margin: 28px auto 0;
}

.qr-success .qr-btn {
    margin-top: 12px;
}

.qr-success-error {
    margin-bottom: 4px;
}

.qr-success-link {
    display: inline-block;
    margin-top: 14px;
    color: var(--accent-ink);
    font-weight: 600;
    text-decoration: underline;
}
</style>
